import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus, BrowserAuthError, AuthError } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../../../authConfig";
import { useAuth } from "../../../AuthProvider";
import { StandardButton } from "../../ui";
import { debugError } from "../../../utils/debug";

const LoginPage: React.FC = () => {
  const { instance, accounts, inProgress } = useMsal();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (
      accounts.length > 0 &&
      isAuthenticated &&
      inProgress === InteractionStatus.None
    ) {
      navigate("/");
    }
  }, [accounts, inProgress, navigate, isAuthenticated]);

  // Map Azure AD error codes to user-friendly messages (without sensitive details)
  const parseAzureError = (errorDescription: string): string => {
    // AADSTS50020 - User not in tenant
    if (errorDescription.includes("AADSTS50020")) {
      return "This account is not authorized to access this application. Please sign in with an authorized account.";
    }
    // AADSTS50105 - User not assigned to application
    if (errorDescription.includes("AADSTS50105")) {
      return "Your account has not been granted access to this application. Please contact your administrator.";
    }
    // AADSTS65004 - User declined consent
    if (errorDescription.includes("AADSTS65004")) {
      return "Access was declined. Please accept the required permissions to continue.";
    }
    // AADSTS70011 - Invalid scope
    if (errorDescription.includes("AADSTS70011")) {
      return "Unable to complete sign-in. Please try again or contact support.";
    }
    // AADSTS700016 - Application not found
    if (errorDescription.includes("AADSTS700016")) {
      return "Application configuration error. Please contact support.";
    }
    // AADSTS530003 - Device compliance required
    if (errorDescription.includes("AADSTS530003")) {
      return "Your device does not meet the security requirements. Please contact your administrator.";
    }
    // AADSTS50076 - MFA required
    if (errorDescription.includes("AADSTS50076")) {
      return "Multi-factor authentication is required. Please complete the verification.";
    }
    // AADSTS50079 - MFA enrollment required
    if (errorDescription.includes("AADSTS50079")) {
      return "You need to set up multi-factor authentication. Please contact your administrator.";
    }
    // Generic fallback - don't expose raw error details
    return "Authentication failed. Please try again or contact support if the problem persists.";
  };

  // Check URL hash for error parameters (when redirected back from Azure AD)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get("error");
      const errorDescription = params.get("error_description");

      if (error && errorDescription) {
        debugError("URL auth error:", error, errorDescription);
        setAuthError(parseAzureError(decodeURIComponent(errorDescription)));
        // Clean up the URL
        window.history.replaceState(null, "", window.location.pathname);
      }
    }
  }, []);

  // Handle redirect errors via MSAL (e.g., AADSTS50020 - user not in tenant)
  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then((response) => {
        if (response) {
          // Successfully authenticated
          setAuthError(null);
        }
      })
      .catch((error: AuthError) => {
        debugError("Redirect error:", error);

        // Extract user-friendly error message
        if (error.errorCode === "user_cancelled") {
          // User cancelled the login - no need to show error
          return;
        }

        // Handle Azure AD errors
        if (error.errorMessage) {
          setAuthError(parseAzureError(error.errorMessage));
        } else {
          setAuthError("Authentication failed. Please try again.");
        }
      });
  }, [instance]);

  const handleMicrosoftLogin = async () => {
    try {
      setAuthError(null);
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      debugError("Login failed:", error);

      if (error instanceof BrowserAuthError) {
        if (error.errorCode === "hash_empty_error") {
          setAuthError(
            "Authentication failed due to a redirect issue. Please try clearing cache and login again."
          );
        } else {
          setAuthError(`Authentication error: ${error.errorMessage}`);
        }
      } else if (error instanceof Error) {
        setAuthError(`Login failed: ${error.message}`);
      } else {
        setAuthError("An unexpected error occurred during login.");
      }
    }
  };

  if (inProgress !== InteractionStatus.None) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#FBFBFB",
        }}
      >
        <div
          style={{
            backgroundColor: "#FFF",
            borderRadius: "12px",
            padding: "40px",
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "3px solid #E5E7EB",
              borderTopColor: "#171C27",
              borderRadius: "50%",
              margin: "0 auto 16px",
              animation: "spin 1s linear infinite",
            }}
          />
          <h2
            style={{
              color: "#63615C",
              fontFamily: "Interstate",
              fontSize: "20px",
              fontWeight: 600,
              marginBottom: "8px",
            }}
          >
            Authentication in progress...
          </h2>
          <p
            style={{
              color: "rgba(99, 97, 92, 0.5)",
              fontFamily: "Interstate",
              fontSize: "14px",
            }}
          >
            Please wait while we verify your credentials.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        gap: "24px",
        padding: "24px",
      }}
    >
      {/* Column 1 - Login Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* Logo - Fixed Position */}
        <div
          className="absolute top-6 left-6 md:top-8 md:left-8 flex items-end"
        >
          <img
            src="/images/mic-logo.png"
            alt="MIC Logo"
            style={{ height: "88px" }}
          />
        </div>

        {/* Centered Content */}
        <div
          className="flex-1 flex flex-col items-start justify-center px-6 md:px-8"
          style={{paddingTop: "120px"}}
        >
          {/* Title */}
          <span
            style={{
              background: "linear-gradient(90deg, var(--color-indigoBlue, #171C27) 0%, var(--color-turquoise, #24AEA8) 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontFamily: "Interstate",
              fontSize: "64px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "normal",
              letterSpacing: "16px",
              marginLeft: "-4px"
            }}
          >
            ALIA
          </span>
          <span
            style={{
              color: "rgba(99, 97, 92, 0.50)",
              fontFamily: "Interstate",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              marginBottom: "32px",

            }}
          >
            AI Solutions for Talent Management
          </span>

          {/* Login Button */}
          <div className="w-full md:w-[440px]">
            <StandardButton
              text="Login"
              variant="fill"
              color="secondary"
              size="large"
              width="100%"
              onClick={handleMicrosoftLogin}
            />
            {/* Error Message */}
            {authError && (
              <p
                style={{
                  color: "#E11313",
                  fontFamily: "Interstate",
                  fontSize: "9px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                  marginTop: "8px",
                }}
              >
                {authError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer
          className="px-6 md:px-8"
          style={{
            paddingTop: "24px",
            paddingBottom: "24px",
          }}
        >
          <div
            style={{
              borderTop: "1px solid #E5E7EB",
              paddingTop: "24px",
              color: "#9CA3AF",
              fontFamily: "Interstate",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            © {new Date().getFullYear()} Mubadala Investment Company. Internal use only.
          </div>
        </footer>
      </div>

      {/* Column 2 - Background Image (hidden on mobile) */}

      <div
        className="hidden md:block"
        style={{
            // display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
            height: "calc(100vh - 48px)",
            width: "50vw",
            backgroundColor: "#FFFFFF",
            paddingLeft: "24px",
          }}>
        <div
          style={{
            flex: 1,
            backgroundImage: "url(/images/login-background.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            borderRadius: "16px",
            position: "relative",
            zIndex: 1,
            height: "100%",
            width: "100%",
          }}
        />
      </div>
    </div>
  );
};

export default LoginPage;
